import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { KeywordPicker } from 'common';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import { EntityRoutes } from 'routes/constants';
import strings from 'strings';
import TextBox from 'UI/components/atoms/TextBox';
import { When } from 'UI/components/atoms/When';
import AutocompleteSelect from 'UI/components/molecules/AutocompleteSelect';
import IndustryFields from 'UI/components/molecules/IndustryFields';
import LocationForm from 'UI/components/organisms/LocationForm';
import { FORM_SPACING, getGridColumnSize, minWidthTextBox } from 'UI/constants/dimensions';
import { Endpoints } from 'UI/constants/endpoints';
import { FeatureFlags } from 'UI/constants/featureFlags';
import {
  EXT_PHONE_VALIDATION,
  NICKNAME_VALIDATION,
  PHONE_VALIDATION,
  REQUIRED_VALIDATION,
  URL_VALIDATION,
  VALIDATION_REGEXS
} from 'UI/utils';
import { areEmailsEqual, buildBasePropsForField } from 'UI/utils/forms';
import { DEFAULT_DISPLAY_TEXT_KEY } from 'UI/utils/inventory';
import { OptionRenderers } from 'UI/utils/renderers';

import PhoneNumbersForm from '../PhoneNumbersForm/PhoneNumbersForm';
import { DEFAULT_PHONE_ROW } from '../PhoneNumbersForm/utils';

import { CHAINED_SELECTS, FormFieldsMap, NAME_TYPE_ID } from './NameForm.constants';
import { useStyles } from './styles';

const getPhoneInputType = phone =>
  phone && phone.length > PHONE_VALIDATION.maxLength.value ? 'text' : 'phone';

/**
 * @param {Object} props
 * @param {Map} props.initialValues
 * @param {boolean} props.fetchingCompany
 * @param {boolean} props.isEditing
 */
const NameForm = ({ initialValues, fetchingCompany, isEditing }) => {
  const location = useLocation();
  const classes = useStyles();
  const { register, unregister, errors, setValue, getValues, watch } = useFormContext();

  const formValues = watch();

  const { checkIfFeatureFlagEnabled } = useFeatureFlags();

  const shouldDisplayLegacyPhoneFields = !checkIfFeatureFlagEnabled(
    FeatureFlags.ContactsMultiplePhones
  );

  const isSubStatusEnabled = checkIfFeatureFlagEnabled(FeatureFlags.HaNameSubStatusEdition);

  useEffect(() => {
    register({ name: FormFieldsMap.COMPANY.key }, REQUIRED_VALIDATION);
    register({ name: FormFieldsMap.STATE.key });
    register({ name: FormFieldsMap.CITY.key });
    register({ name: FormFieldsMap.ZIP.key });
    register({ name: FormFieldsMap.SOURCE.key });
    register({ name: FormFieldsMap.NAME_STATUS.key }, REQUIRED_VALIDATION);
    register({ name: FormFieldsMap.PHONE.key }, { ...PHONE_VALIDATION });
    if (shouldDisplayLegacyPhoneFields) {
      register({ name: FormFieldsMap.OTHER_PHONE.key }, { ...PHONE_VALIDATION });
    }
    register({
      name: FormFieldsMap.PHONES.key,
      value: [DEFAULT_PHONE_ROW]
    });
    register({ name: FormFieldsMap.KeywordTags.key });
  }, [register, shouldDisplayLegacyPhoneFields]);

  useEffect(() => {
    if (isSubStatusEnabled) {
      register({ name: FormFieldsMap.SUB_STATUS.key });
    } else {
      unregister(FormFieldsMap.SUB_STATUS.key);
    }

    return () => unregister(FormFieldsMap.SUB_STATUS.key);
  }, [isSubStatusEnabled, register, unregister]);

  useEffect(() => {
    if (!initialValues?.company_id) return;
    setValue(FormFieldsMap.COMPANY.key, initialValues.company_id);
  }, [initialValues.company_id, setValue]);

  const handleComboChange = (name, value) => {
    setValue(name, value, true);

    if (name && CHAINED_SELECTS[name]) {
      CHAINED_SELECTS[name].forEach(chainedSelect => {
        setValue(chainedSelect, null);
      });
    }
  };

  const handlePhoneChange = (name, value) => {
    setValue(name, value, true);
  };

  const handleChangeKeywords = (name, value) => {
    setValue(name, value, true);
  };

  const { sm, xs, md, lg } = getGridColumnSize(isEditing);

  const nameEndpoint = Endpoints.SimpleStatuses.replace(':nameTypeId', NAME_TYPE_ID);
  const statusEndpoint = `${nameEndpoint}&selectable=true`;
  const statusId = formValues[FormFieldsMap.NAME_STATUS.key]?.id;
  const subStatusEndpoint =
    isSubStatusEnabled && statusId ? `${Endpoints.NameSubStatuses}?statusId=${statusId}` : null;

  return (
    <Grid container className="creation-form-layout">
      <Grid container item spacing={FORM_SPACING}>
        <Grid item xs={xs} sm={sm} md={md} lg={lg}>
          <AutocompleteSelect
            {...buildBasePropsForField(FormFieldsMap.COMPANY.key, errors)}
            fetching={fetchingCompany}
            selectedValue={formValues[FormFieldsMap.COMPANY.key]}
            displayKey="name"
            placeholder="Company *"
            typeahead
            typeaheadLimit={50}
            typeaheadParams={{ entityType: 'company', isForConvertion: true }}
            url={Endpoints.Search}
            createButton={{
              text: 'Create company',
              concatKeyword: true,
              url: EntityRoutes.CompanyCreate,
              showAlways: true,
              location
            }}
            renderOption={OptionRenderers.globalSearchDefault('name')}
            onSelect={handleComboChange}
            noMargin
          />
        </Grid>
        <Grid item xs={xs} sm={sm} md={md} lg={lg}>
          <TextBox
            {...buildBasePropsForField(FormFieldsMap.FIRST_NAME.key, errors)}
            label="First Name *"
            inputRef={register(REQUIRED_VALIDATION)}
            minWidth={minWidthTextBox}
            noMargin
            inputProps={{ 'aria-label': 'first name' }}
          />
        </Grid>
        <Grid item xs={xs} sm={sm} md={md} lg={lg}>
          <TextBox
            {...buildBasePropsForField(FormFieldsMap.LAST_NAME.key, errors)}
            label="Last Name *"
            inputRef={register(REQUIRED_VALIDATION)}
            minWidth={minWidthTextBox}
            noMargin
            inputProps={{ 'aria-label': 'last name' }}
          />
        </Grid>
        <Grid item xs={xs} sm={sm} md={md} lg={lg}>
          <TextBox
            {...buildBasePropsForField(FormFieldsMap.NICKNAME.key, errors)}
            label="Nickname"
            inputRef={register(NICKNAME_VALIDATION)}
            minWidth={minWidthTextBox}
          />
        </Grid>
        <Grid item xs={xs} sm={sm} md={md} lg={lg}>
          <TextBox
            {...buildBasePropsForField(FormFieldsMap.EMAIL.key, errors)}
            label="Email"
            inputRef={register({
              pattern: {
                value: VALIDATION_REGEXS.EMAIL,
                message: 'Email must be valid'
              },
              validate: {
                notEqual: value =>
                  areEmailsEqual(value, watch(FormFieldsMap.OTHER_EMAIL.key))
                    ? strings.shared.validations.otherEmail
                    : true
              }
            })}
            minWidth={minWidthTextBox}
            noMargin
          />
        </Grid>
        <IndustryFields isEditing={isEditing} disabled={false} onSelect={handleComboChange} />
        <Grid item xs={xs} sm={sm} md={md} lg={lg}>
          <TextBox
            {...buildBasePropsForField(FormFieldsMap.TITLE.key, errors)}
            label="Title *"
            inputRef={register(REQUIRED_VALIDATION)}
            minWidth={minWidthTextBox}
            noMargin
            inputProps={{ 'aria-label': 'name title' }}
          />
        </Grid>

        {shouldDisplayLegacyPhoneFields && (
          <>
            <Grid item xs={xs} sm={sm} md={md} lg={lg}>
              <TextBox
                {...buildBasePropsForField(FormFieldsMap.PHONE.key, errors)}
                label="Phone"
                placeholder="(999)-999-9999"
                minWidth={minWidthTextBox}
                onChange={handlePhoneChange}
                value={getValues('phone') || ''}
                inputType={getPhoneInputType(formValues.phone)}
                noMargin
              />
            </Grid>
            <Grid item xs={xs} sm={sm} md={md} lg={lg}>
              <TextBox
                {...buildBasePropsForField(FormFieldsMap.EXT.key, errors)}
                label="Ext"
                placeholder="999"
                inputRef={register({ ...EXT_PHONE_VALIDATION })}
                minWidth={minWidthTextBox}
                noMargin
              />
            </Grid>
            <Grid item xs={xs} sm={sm} md={md} lg={lg}>
              <TextBox
                {...buildBasePropsForField(FormFieldsMap.OTHER_PHONE.key, errors)}
                label="Other phone"
                placeholder="(999)-999-9999"
                minWidth={minWidthTextBox}
                onChange={handlePhoneChange}
                value={getValues(FormFieldsMap.OTHER_PHONE.key) || ''}
                inputType={getPhoneInputType(formValues.mobile)}
                noMargin
              />
            </Grid>
          </>
        )}
        <LocationForm
          state={formValues[FormFieldsMap.STATE.key]}
          city={formValues[FormFieldsMap.CITY.key]}
          zip={formValues[FormFieldsMap.ZIP.key]}
          handleComboChange={handleComboChange}
          isEditing={isEditing}
          removeRequiredMark
        />
        <Grid item xs={xs} sm={sm} md={md} lg={lg}>
          <TextBox
            {...buildBasePropsForField(FormFieldsMap.OTHER_EMAIL.key, errors)}
            label="Other email"
            inputRef={register({
              pattern: {
                value: VALIDATION_REGEXS.EMAIL,
                message: 'Email must be valid'
              },
              validate: {
                notEqual: value =>
                  areEmailsEqual(value, watch(FormFieldsMap.EMAIL.key))
                    ? strings.shared.validations.otherEmail
                    : true
              }
            })}
            minWidth={minWidthTextBox}
            noMargin
          />
        </Grid>
        <Grid item xs={xs} sm={sm} md={md} lg={lg}>
          <AutocompleteSelect
            {...buildBasePropsForField(FormFieldsMap.SOURCE.key, errors)}
            selectedValue={formValues[FormFieldsMap.SOURCE.key]}
            placeholder="Source"
            url={Endpoints.SourceTypes}
            onSelect={handleComboChange}
            noMargin
          />
        </Grid>
        <Grid item xs={xs} sm={sm} md={md} lg={lg}>
          <TextBox
            {...buildBasePropsForField(FormFieldsMap.LINKEDIN_URL.key, errors)}
            label="Source URL"
            inputRef={register(URL_VALIDATION)}
            minWidth={minWidthTextBox}
            noMargin
          />
        </Grid>
        <Grid item xs={xs} sm={sm} md={md} lg={lg}>
          <AutocompleteSelect
            {...buildBasePropsForField(FormFieldsMap.NAME_STATUS.key, errors)}
            name={FormFieldsMap.NAME_STATUS.key}
            onSelect={handleComboChange}
            placeholder={`${strings.shared.fields.status} *`}
            selectedValue={formValues[FormFieldsMap.NAME_STATUS.key]}
            url={statusEndpoint}
          />
        </Grid>
        <When condition={isSubStatusEnabled}>
          <Grid item xs={xs} sm={sm} md={md} lg={lg}>
            <AutocompleteSelect
              {...buildBasePropsForField(FormFieldsMap.SUB_STATUS.key, errors)}
              name={FormFieldsMap.SUB_STATUS.key}
              onSelect={handleComboChange}
              placeholder={strings.shared.fields.subStatus}
              selectedValue={formValues[FormFieldsMap.SUB_STATUS.key]}
              url={subStatusEndpoint}
            />
          </Grid>
        </When>
        <input ref={register()} type="hidden" name="status_id" value="1" />
      </Grid>

      <Grid container spacing={2} className={classes.keywordsContainer}>
        <Grid item xs={12}>
          <Typography variant="subtitle1">
            {strings.inventoryProfiles.common.keywords.title}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <KeywordPicker
            allowNewItems
            displayTextKey={DEFAULT_DISPLAY_TEXT_KEY}
            endpoint={Endpoints.KeywordTags}
            name={FormFieldsMap.KeywordTags.key}
            onSelect={handleChangeKeywords}
            placeholder={strings.inventoryProfiles.common.keywords.placeholder}
            selectedValue={formValues?.[FormFieldsMap.KeywordTags.key] ?? []}
            typeaheadLimit={25}
          />
        </Grid>
      </Grid>

      {!shouldDisplayLegacyPhoneFields && <PhoneNumbersForm isEditing={isEditing} />}
    </Grid>
  );
};

NameForm.defaultProps = {
  initialValues: {},
  isEditing: false
};

export default NameForm;
